import React from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { format, subDays, eachDayOfInterval, eachHourOfInterval, differenceInHours } from "date-fns";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Activity,
  Wallet,
  Calendar,
  RefreshCw,
  Users,
  Server,
} from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import { getDateRange, filterTransactionsByDateRange } from "@/shared/utils/dateFilters";

export default function ResellerDashboard() {
  const { t } = useLanguage();
  const [dateFilter, setDateFilter] = React.useState("today");
  const [customDateRange, setCustomDateRange] = React.useState({ from: null, to: null });
  const resellerName = "Asia Gaming Network";

  // TODO: Implement custom API calls
  const { data: merchants = [], refetch } = useQuery({
    queryKey: ["reseller-merchants"],
    queryFn: async () => [], // TODO: Implement Merchant.list API call
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ["reseller-transactions"],
    queryFn: async () => [], // TODO: Implement Transaction.list API call
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => [], // TODO: Implement Provider.list API call
  });

  // Get date range based on filter
  const dateRange = React.useMemo(() => {
    return getDateRange(dateFilter, customDateRange);
  }, [dateFilter, customDateRange]);

  // Filter transactions by date range
  const filteredTransactions = React.useMemo(() => {
    return filterTransactionsByDateRange(
      transactions,
      dateRange.startDate,
      dateRange.endDate
    );
  }, [transactions, dateRange]);

  // Calculate stats
  const totalVolume = filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const successDeposits = filteredTransactions.filter(t => t.type === "deposit" && t.status === "success");
  const totalDeposits = successDeposits.reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalWithdrawals = filteredTransactions.filter(t => t.type === "withdrawal" && t.status === "success").reduce((sum, t) => sum + (t.amount || 0), 0);
  const activeMerchants = merchants.filter(m => m.status === "active").length;
  const successRate = filteredTransactions.length > 0
    ? ((filteredTransactions.filter(t => t.status === "success").length / filteredTransactions.length) * 100).toFixed(1)
    : 100;

  // Generate chart data based on date range
  const chartData = React.useMemo(() => {
    const { startDate, endDate } = dateRange;
    const hoursDiff = differenceInHours(endDate, startDate);

    // If range is less than 2 days, show hourly data
    if (hoursDiff <= 48) {
      const hours = eachHourOfInterval({ start: startDate, end: endDate });
      return hours.map((hour) => {
        const hourTxs = filteredTransactions.filter(
          (t) => {
            const txDate = new Date(t.created_date);
            return txDate >= hour && txDate < new Date(hour.getTime() + 60 * 60 * 1000) && t.status === "success";
          }
        );
        return {
          date: format(hour, "HH:mm"),
          deposits: hourTxs.filter(t => t.type === "deposit").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
          withdrawals: hourTxs.filter(t => t.type === "withdrawal").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
        };
      });
    } else {
      // Show daily data
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      return days.map((day) => {
        const dayTxs = filteredTransactions.filter(t => {
          const txDate = new Date(t.created_date);
          return txDate >= day && txDate < new Date(day.getTime() + 24 * 60 * 60 * 1000) && t.status === "success";
        });
        return {
          date: format(day, "MMM d"),
          deposits: dayTxs.filter(t => t.type === "deposit").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
          withdrawals: dayTxs.filter(t => t.type === "withdrawal").reduce((sum, t) => sum + (t.amount || 0), 0) / 1000,
        };
      });
    }
  }, [filteredTransactions, dateRange]);

  const recentTransactions = filteredTransactions.slice(0, 8);

  const statusColors = {
    success: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    processing: "bg-blue-100 text-blue-700",
    failed: "bg-red-100 text-red-700",
  };

  return (
    <ResellerLayout currentPage="ResellerDashboard" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("dashboard")}</h1>
            <p className="text-slate-500">{t("welcomeBack")}, {resellerName}</p>
          </div>
          <div className="flex items-center gap-3">
            <DateFilter
              value={dateFilter}
              onChange={setDateFilter}
              customDateRange={customDateRange}
              onCustomDateRangeChange={setCustomDateRange}
            />
            <Button variant="outline" size="icon" onClick={() => refetch()} className="bg-white">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { title: t("totalVolume"), value: `฿${totalVolume.toLocaleString()}`, icon: Wallet, color: "bg-violet-100 text-violet-600" },
            { title: t("deposits"), value: `฿${totalDeposits.toLocaleString()}`, icon: ArrowDownToLine, color: "bg-blue-100 text-blue-600" },
            { title: t("withdrawals"), value: `฿${totalWithdrawals.toLocaleString()}`, icon: ArrowUpFromLine, color: "bg-emerald-100 text-emerald-600" },
            { title: t("successRate"), value: `${successRate}%`, icon: CheckCircle2, color: "bg-green-100 text-green-600" },
            { title: t("merchants"), value: activeMerchants, icon: Building2, color: "bg-amber-100 text-amber-600" },
          ].map((stat, i) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-slate-100"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-500">{stat.title}</p>
                  <p className="text-lg lg:text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="h-5 w-5 lg:h-6 lg:w-6" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Charts & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Volume Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="lg:col-span-2 bg-white rounded-2xl p-4 lg:p-6 shadow-sm border border-slate-100"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{t("volumeOverviewTitle")}</h3>
                <p className="text-sm text-slate-500">{t("transactionVolume")}</p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-violet-500" />
                  <span className="text-slate-600">{t("deposits")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-slate-600">{t("withdrawals")}</span>
                </div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="depositsGradientR" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="withdrawalsGradientR" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `${v}K`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                    }}
                    formatter={(value) => [`฿${(value * 1000).toLocaleString()}`, ""]}
                  />
                  <Area type="monotone" dataKey="deposits" stroke="#8b5cf6" strokeWidth={2} fill="url(#depositsGradientR)" />
                  <Area type="monotone" dataKey="withdrawals" stroke="#10b981" strokeWidth={2} fill="url(#withdrawalsGradientR)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Recent Transactions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm border border-slate-100"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{t("recent")}</h3>
              <Link to={createPageUrl("ResellerTransactions")} className="text-sm text-violet-600 hover:text-violet-700">
                {t("viewAll")}
              </Link>
            </div>
            <div className="space-y-3">
              {recentTransactions.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">{t("noTransactionsYet")}</p>
              ) : (
                recentTransactions.map((tx, index) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35 + index * 0.03 }}
                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx.type === "deposit" ? "bg-violet-100" : "bg-emerald-100"
                      }`}>
                      {tx.type === "deposit" ? (
                        <ArrowDownToLine className="h-4 w-4 text-violet-600" />
                      ) : (
                        <ArrowUpFromLine className="h-4 w-4 text-emerald-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{tx.merchant_code || t("nA")}</p>
                      <p className="text-xs text-slate-500">
                        {tx.created_date ? format(new Date(tx.created_date), "HH:mm") : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${tx.type === "deposit" ? "text-violet-600" : "text-emerald-600"}`}>
                        ฿{tx.amount?.toLocaleString()}
                      </p>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">{t("quickActions")}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Link to={createPageUrl("ResellerMerchants")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Building2 className="h-5 w-5 text-violet-600" />
                <span className="text-sm">{t("merchants")}</span>
              </Button>
            </Link>
            <Link to={createPageUrl("ResellerTransactions")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                <span className="text-sm">{t("transactions")}</span>
              </Button>
            </Link>
            <Link to={createPageUrl("ResellerRouting")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <span className="text-sm">{t("routing")}</span>
              </Button>
            </Link>
            <Link to={createPageUrl("ResellerProviders")}>
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                <Server className="h-5 w-5 text-amber-600" />
                <span className="text-sm">{t("providers")}</span>
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </ResellerLayout>
  );
}